import * as React from 'react';
import classnames from 'classnames';

import ActionButton from 'components/buttons/action-button/ActionButton';
import ImageGrid from 'components/image-grid/ImageGrid';
import SVGDynamicIcon from 'components/svg-dynamic-icon/SVGDynamicIcon';
import SVGIcon from 'components/svg-icon/SVGIcon';
import { withModal } from 'components/modal/ModalHOC';

import ContentCardContext from 'components/cards/content-card/context/ContentCardContext';

import { __t, applyTranslations } from 'utils/language';
import { WITH_GALLERY_MODAL_CONFIG } from 'utils/gallery';
import {
  CHANNEL_TYPE_TIKTOK,
  CHANNEL_TYPE_YOUTUBE,
  getTiktokEmbedUrl,
  getYoutubeEmbedUrl
} from 'utils/channels';
import {
  DELIVERABLE_TYPE_INSTAGRAM_REEL as IG_REEL_TYPE,
  DELIVERABLE_TYPE_INSTAGRAM_STORY as IG_STORY_TYPE,
  DELIVERABLE_TYPE_TIKTOK_VIDEO as TT_VIDEO_TYPE,
  DELIVERABLE_TYPE_YOUTUBE_VIDEO as YT_VIDEO_TYPE
} from 'utils/deliverables';

import './content-card-asset-item.scss';

import type { ChannelTypes, GalleryModalItem, GalleryModalItemType } from 'types';
import type {
  ContentCardItem
} from 'components/cards/content-card/types';

applyTranslations(require('components/cards/content-card/shared/content-card-shared.i18n.json'));

type Props = {
  item: ContentCardItem,
  index: number,
  channel: ChannelTypes,
  toggleStats: boolean,
  onNoPostClick?: () => void
};

const DivWithModal = withModal(<div />, WITH_GALLERY_MODAL_CONFIG);
const SHARED_MSG_KEY = 'content_card_shared';

const getAssetUrl = (
  channel: ChannelTypes,
  url: string
) => {
  switch (channel) {
    case CHANNEL_TYPE_TIKTOK:
      return { embedUrl: getTiktokEmbedUrl(url) };
    case CHANNEL_TYPE_YOUTUBE:
      return { embedUrl: getYoutubeEmbedUrl(url) };
    default:
      return {};
  }
};

const getGalleryModalItems = (
  items: Array<ContentCardItem>,
  channel: ChannelTypes
): Array<GalleryModalItem> => (
  (items || []).map(item => ({
    type: channel as GalleryModalItemType,
    id: item.id,
    url: item.url || '',
    assetUrl: item.thumbnailUrl || item.imageUrl || '',
    ...getAssetUrl(channel, item.url || '')
  }))
);

const ContentCardAssetItem = ({
  item,
  index,
  channel,
  toggleStats,
  onNoPostClick
}: Props): JSX.Element => {
  const context = React.useContext(ContentCardContext);

  const renderIGStory = (): JSX.Element => (
    <div className="content-card-asset-item">
      <ImageGrid
        size={228}
        gutter={2}
        attachments={item.postAttachments}
      />
    </div>
  );

  if (item.type === IG_STORY_TYPE) {
    return renderIGStory();
  }

  const playIconClasses = classnames('content-card-asset-item__play', {
    'content-card-asset-item__play--is-visible': !toggleStats
  });

  const assetUrl = item.thumbnailUrl || item.imageUrl || '';
  const isVideo = item.type === TT_VIDEO_TYPE || item.type === YT_VIDEO_TYPE || item.type === IG_REEL_TYPE;

  const handleNoPostClick = (event: React.ChangeEvent<HTMLElement>): void => {
    event.stopPropagation();

    if (onNoPostClick) {
      onNoPostClick();
    }
  };

  const renderNoPost = (): JSX.Element => {
    const postType = isVideo ? 'video' : 'post';

    return (
      <div className="content-card-asset-item__no-post">
        <div className="content-card-asset-item__no-post-icon">
          <SVGDynamicIcon
            name="exclamation-inside-circle-custom-bg-white-fg"
            width={24}
            height={24}
            customColor="red"
          />
        </div>
        <div className="content-card-asset-item__no-post-text">
          {__t(`${SHARED_MSG_KEY}.messages.no_post_url`, {
            type: __t(`${SHARED_MSG_KEY}.labels.${postType}`)
          })}
        </div>
        <div className="content-card-asset-item__no-post-button-container">
          <ActionButton
            color="secondary"
            width="minimum"
            className="content-card-asset-item__no-post-button"
            onClick={handleNoPostClick}
          >
            {__t(`${SHARED_MSG_KEY}.buttons.open_metrics`)}
          </ActionButton>
        </div>
      </div>
    );
  };

  const renderPost = (url: string): JSX.Element => (
    <div
      className="content-card-asset-item__image"
      style={{ backgroundImage: `url(${url})` }}
    />
  );

  const triggerClasses = classnames('content-card-asset-item__trigger lazyload', {
    'content-card-asset-item__trigger--no-post': !assetUrl
  });

  return (
    <div className="content-card-asset-item">
      <DivWithModal
        key={`content-card-asset-item__trigger-${String(item.id)}`}
        modalContentProps={{
          items: getGalleryModalItems(context?.posts || [], channel),
          currentPosition: index
        }}
        className={triggerClasses}
      >
        {
          !assetUrl
            ? renderNoPost()
            : renderPost(assetUrl)
        }
        {
          isVideo && assetUrl && (
            <div className={playIconClasses}>
              <SVGIcon
                name="play-button"
                width={40}
                height={40}
              />
            </div>
          )
        }
      </DivWithModal>
    </div>
  );
};

export default ContentCardAssetItem;
